import { startTransition, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Wrap,
  useColorModeValue
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import Page from '../components/Page';
import useUserQuery from '../components/api/queries/useUserQuery';
import RegularSolidButton from '../components/buttons/RegularSolidButton';
import DiaryBookCover from '../components/photoCardDiary/DiaryBookCover';
import { ENABLE_DIARY_COVER_CUSTOMIZATION } from '../constants/photoCardDiary';
import { getArtistId } from '../utils/etcUtils';
import { saveData } from '../utils/saveData';
import { getDiaryCover, loadDiaryCoverMap } from '../utils/photoCardDiaryCoverStorage';
import { queryGet } from '../utils/queryClient';

const DIARY_BOARD_COVER_WIDTH = 260;
const DIARY_BOARD_COVER_HEIGHT = 362;
const DIARY_BOARD_COVER_SCALE = 0.6;
const DIARY_BOARD_COVER_SCALED_WIDTH = `calc(${DIARY_BOARD_COVER_WIDTH}px * ${DIARY_BOARD_COVER_SCALE})`;
const DIARY_BOARD_COVER_SCALED_HEIGHT = `calc(${DIARY_BOARD_COVER_HEIGHT}px * ${DIARY_BOARD_COVER_SCALE})`;
const HEADER_TITLE_SCROLL_THRESHOLD = 48;
const DIARY_BOARD_SKELETON_KEYS = ['left', 'right'];

function DiaryCoverCard({
  cover,
  member,
  artistName,
  badgeText,
  metaText,
  ribbonText,
  isLocked = false,
  onClick,
  onEdit
}) {
  return (
    <Stack spacing={'12px'} width={DIARY_BOARD_COVER_SCALED_WIDTH}>
      <Box
        position={'relative'}
        width={DIARY_BOARD_COVER_SCALED_WIDTH}
        height={DIARY_BOARD_COVER_SCALED_HEIGHT}
        overflow={'hidden'}
        borderTopLeftRadius={'2px'}
        borderTopRightRadius={'11px'}
        borderBottomRightRadius={'11px'}
        borderBottomLeftRadius={'2px'}
      >
        {metaText
          ? <Box
              position={'absolute'}
              top={'10px'}
              right={'-22px'}
              zIndex={2}
              width={'84px'}
              height={'20px'}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              paddingX={'0px'}
              transform={'rotate(41deg)'}
              backgroundColor={'rgba(255,255,255,0.82)'}
              borderTop={'1px solid rgba(255,255,255,0.56)'}
              borderBottom={'1px solid rgba(0,0,0,0.12)'}
              boxShadow={'0 4px 10px rgba(17,17,17,0.16)'}
              pointerEvents={'none'}
            >
              <Text
                width={'100%'}
                color={'rgba(97,102,112,0.96)'}
                fontSize={'10px'}
                fontWeight={700}
                lineHeight={1}
                letterSpacing={'-0.02em'}
                textAlign={'center'}
                textShadow={'0 1px 0 rgba(255,255,255,0.28)'}
                transform={'translateX(-3px)'}
              >
                {metaText}
              </Text>
            </Box>
          : null}

        <Box
          as="button"
          type="button"
          onClick={isLocked ? undefined : onClick}
          display={'block'}
          width={DIARY_BOARD_COVER_SCALED_WIDTH}
          height={DIARY_BOARD_COVER_SCALED_HEIGHT}
          padding={'0'}
          border={'0'}
          background={'transparent'}
          textAlign={'left'}
          cursor={isLocked ? 'default' : 'pointer'}
          aria-disabled={isLocked}
          tabIndex={isLocked ? -1 : 0}
        >
          <Box
            width={`${DIARY_BOARD_COVER_WIDTH}px`}
            height={`${DIARY_BOARD_COVER_HEIGHT}px`}
            transform={`scale(${DIARY_BOARD_COVER_SCALE})`}
            transformOrigin={'top left'}
          >
            <DiaryBookCover
              cover={cover}
              memberName={member.name}
              subtitle={artistName || undefined}
              ribbonText={ribbonText}
              badgeText={badgeText}
            />
          </Box>
        </Box>
      </Box>

      {onEdit
        ? <Button
            size="sm"
            width={DIARY_BOARD_COVER_SCALED_WIDTH}
            minWidth={DIARY_BOARD_COVER_SCALED_WIDTH}
            height={'40px'}
            justifyContent={'center'}
            borderRadius={'999px'}
            backgroundColor={'rgba(17,17,17,0.06)'}
            onClick={onEdit}
          >
            {`💎 ${t('myPhotoCardDiary.editCoverButton', { defaultValue: '표지 꾸미기' })}`}
          </Button>
        : null}
    </Stack>
  );
}

function DiaryCoverCardSkeleton({ startColor, endColor }) {
  return (
    <Stack spacing={'12px'} width={DIARY_BOARD_COVER_SCALED_WIDTH}>
      <Skeleton
        width={DIARY_BOARD_COVER_SCALED_WIDTH}
        height={DIARY_BOARD_COVER_SCALED_HEIGHT}
        borderTopLeftRadius={'2px'}
        borderTopRightRadius={'11px'}
        borderBottomRightRadius={'11px'}
        borderBottomLeftRadius={'2px'}
        fadeDuration={0.12}
        startColor={startColor}
        endColor={endColor}
      />
      {ENABLE_DIARY_COVER_CUSTOMIZATION
        ? <Skeleton
            width={DIARY_BOARD_COVER_SCALED_WIDTH}
            height={'40px'}
            borderRadius={'999px'}
            fadeDuration={0.12}
            startColor={startColor}
            endColor={endColor}
          />
        : null}
    </Stack>
  );
}

export default function MyPhotoCardBoardPage() {
  const pageRef = useRef(null);
  const navigate = useNavigate();
  const { user_id: userId } = useParams();
  const [selectedArtistId, setSelectedArtistId] = useState(() => getArtistId() || '');
  const { data: artists = [], isLoading: isArtistLoading } = useQuery(
    ['binderArtists'],
    () => queryGet('/api/v1/artists?liked_only=true'),
    {
      select: (data) => data.artists || []
    }
  );
  const selectedArtist = useMemo(() => {
    return artists.find((artist) => artist.artistId === selectedArtistId) || null;
  }, [artists, selectedArtistId]);
  const user = useUserQuery();
  const likedMembers = useMemo(() => {
    const members = selectedArtist?.members || [];
    const filteredMembers = members.filter((member) => member.isLiked);
    return filteredMembers.length > 0 ? filteredMembers : members;
  }, [selectedArtist]);
  const artistName = selectedArtist?.shortName || selectedArtist?.name;
  const collectorName =
    user?.userId === userId
      ? user?.baseUserProfile?.nickname || user?.nickname || undefined
      : undefined;
  const skeletonStartColor = useColorModeValue('rgba(17,17,17,0.018)', 'rgba(255,255,255,0.045)');
  const skeletonEndColor = useColorModeValue('rgba(17,17,17,0.04)', 'rgba(255,255,255,0.085)');
  const pageTitle = t('myPhotoCardDiary.pageTitle', { defaultValue: '포토카드 바인더' });
  const pageHeading = t('myPhotoCardDiary.heading', { defaultValue: '포토카드 바인더' });
  const pageDescription = collectorName
    ? t('myPhotoCardDiary.subheadingWithNickname', {
        nickname: collectorName,
        defaultValue: '{{nickname}}님이 모은 포카를 한곳에 정리했어요.'
      })
    : t('myPhotoCardDiary.subheading', {
        defaultValue: '모은 포카를 한곳에 정리했어요.'
      });

  const [showHeaderTitle, setShowHeaderTitle] = useState(false);
  const coverMap = useMemo(() => {
    return loadDiaryCoverMap({ userId, artistId: selectedArtistId });
  }, [selectedArtistId, userId]);
  const memberIdsKey = useMemo(() => {
    return likedMembers.map((member) => member.memberId).join(',');
  }, [likedMembers]);

  const { data: memberCollectedCountMap = {}, isLoading: isCollectedCountLoading } = useQuery(
    ['collectedPhotoCardIds', selectedArtistId, 'binderBoard', memberIdsKey],
    async () => {
      const collectedCountEntries = await Promise.all(
        likedMembers.map(async (member) => {
          const queryParams = new URLSearchParams({
            member_id: member.memberId
          });
          const collectedPhotoCardData = await queryGet(
            `/api/v1/artists/${selectedArtistId}/photo_cards/collected_ids?${queryParams.toString()}`
          );

          return [member.memberId, collectedPhotoCardData?.collectedPhotoCardIds?.length || 0];
        })
      );

      return Object.fromEntries(collectedCountEntries);
    },
    {
      enabled: !!selectedArtistId && likedMembers.length > 0
    }
  );

  useEffect(() => {
    if (artists.length === 0) return;

    const nextArtist = selectedArtist || artists[0];
    if (!nextArtist || nextArtist.artistId === selectedArtistId) return;

    saveData('artist', nextArtist.artistId);
    setSelectedArtistId(nextArtist.artistId);
  }, [artists, selectedArtist, selectedArtistId]);

  useEffect(() => {
    const container = pageRef.current;

    const updateHeaderTitleVisibility = () => {
      const containerScrollTop = container?.scrollTop || 0;
      const windowScrollTop =
        window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const scrollTop = Math.max(containerScrollTop, windowScrollTop);

      setShowHeaderTitle(scrollTop > HEADER_TITLE_SCROLL_THRESHOLD);
    };

    updateHeaderTitleVisibility();

    container?.addEventListener('scroll', updateHeaderTitleVisibility);
    window.addEventListener('scroll', updateHeaderTitleVisibility);

    return () => {
      container?.removeEventListener('scroll', updateHeaderTitleVisibility);
      window.removeEventListener('scroll', updateHeaderTitleVisibility);
    };
  }, []);

  const handleArtistSelect = (artist) => {
    if (!artist || artist.artistId === selectedArtistId) return;

    saveData('artist', artist.artistId);
    startTransition(() => {
      setSelectedArtistId(artist.artistId);
    });
  };

  const isBoardLoading = isArtistLoading && artists.length === 0;
  const hasArtists = artists.length > 0;

  return (
    <Page ref={pageRef} title={pageTitle} showTitle={showHeaderTitle} showBackButton={true}>
      <Box
        display={'flex'}
        flexDirection={'column'}
        minHeight={'calc((var(--vh, 1vh) * 100) - (44px + env(safe-area-inset-top)) - 48px)'}
      >
        <Stack spacing={'24px'} flex={1}>
          <Stack spacing={'6px'} paddingTop={'12px'} paddingBottom={'12px'} alignItems={'center'}>
            <Text
              className="text-main-light-1 dark:text-main-dark-1"
              fontSize={'28px'}
              fontWeight={700}
              textAlign={'center'}
            >
              {pageHeading}
            </Text>
            <Text
              className="text-main-light-3 dark:text-main-dark-3"
              fontSize={'15px'}
              textAlign={'center'}
            >
              {pageDescription}
            </Text>
          </Stack>

          {!isBoardLoading && hasArtists && (
            <Wrap spacing={'8px'} justify={'flex-start'} width={'100%'}>
              {artists.map((artist) => (
                <RegularSolidButton
                  key={artist.artistId}
                  text={artist.shortName || artist.name}
                  filled={artist.artistId === selectedArtistId}
                  onClicked={() => handleArtistSelect(artist)}
                />
              ))}
            </Wrap>
          )}

          {!isBoardLoading && hasArtists && isCollectedCountLoading && (
            <Text
              className="text-main-light-3 dark:text-main-dark-3"
              fontSize={'13px'}
              textAlign={'center'}
            >
              {t('myPhotoCardDiary.loadingMeta', {
                defaultValue: '정보를 확인하는 중...'
              })}
            </Text>
          )}

          {isBoardLoading
            ? <SimpleGrid
                columns={{ base: 2, md: 2 }}
                spacing={{ base: '12px', md: '18px' }}
                justifyItems={'center'}
              >
                {DIARY_BOARD_SKELETON_KEYS.map((key) => (
                  <DiaryCoverCardSkeleton
                    key={key}
                    startColor={skeletonStartColor}
                    endColor={skeletonEndColor}
                  />
                ))}
              </SimpleGrid>
            : hasArtists
              ? <SimpleGrid
                  columns={{ base: 2, md: 2 }}
                  spacing={{ base: '12px', md: '18px' }}
                  justifyItems={'center'}
                >
                  {likedMembers.map((member, memberIndex) => {
                    const cover = getDiaryCover({
                      coverMap,
                      member,
                      memberIndex
                    });
                    const memberCollectedCount = Object.hasOwn(
                      memberCollectedCountMap,
                      member.memberId
                    )
                      ? memberCollectedCountMap[member.memberId]
                      : undefined;
                    const isCountReady = typeof memberCollectedCount === 'number';
                    const memberCollectedCountText =
                      typeof memberCollectedCount === 'number' && memberCollectedCount > 0
                        ? t('myPhotoCardDiary.coverCount', {
                            count: memberCollectedCount
                          })
                        : undefined;
                    const emptyBinderRibbonText =
                      memberCollectedCount === 0
                        ? t('myPhotoCardDiary.emptyBinderRibbon', {
                            defaultValue: '아직 모은 포카가 없어 열 수 없어요'
                          })
                        : undefined;

                    return (
                      <DiaryCoverCard
                        key={member.memberId}
                        member={member}
                        cover={cover}
                        artistName={artistName}
                        metaText={memberCollectedCountText}
                        ribbonText={emptyBinderRibbonText}
                        isLocked={isCountReady && memberCollectedCount === 0}
                        badgeText={collectorName}
                        onClick={() => navigate(`/users/${userId}/binder/${member.memberId}`)}
                        onEdit={
                          ENABLE_DIARY_COVER_CUSTOMIZATION
                            ? () => navigate(`/users/${userId}/binder/${member.memberId}/cover`)
                            : undefined
                        }
                      />
                    );
                  })}
                </SimpleGrid>
              : null}

          {!isBoardLoading && !hasArtists && (
            <Box
              borderRadius={'24px'}
              padding={'24px'}
              className="bg-surface-light dark:bg-backgroundElevated-dark"
            >
              <Text className="text-main-light-3 dark:text-main-dark-3">
                {t('myPhotoCardDiary.emptyArtists', {
                  defaultValue: '좋아요한 아티스트가 없어요.'
                })}
              </Text>
            </Box>
          )}

          {!isBoardLoading && hasArtists && likedMembers.length === 0 && (
            <Box
              borderRadius={'24px'}
              padding={'24px'}
              className="bg-surface-light dark:bg-backgroundElevated-dark"
            >
              <Text className="text-main-light-3 dark:text-main-dark-3">
                {t('myPhotoCardDiary.emptyMembers', {
                  defaultValue: '아직 다이어리로 열 멤버가 없어요.'
                })}
              </Text>
            </Box>
          )}
        </Stack>

        <Box marginTop={'auto'} paddingTop={'32px'}>
          <Box
            marginX={'-24px'}
            marginBottom={'-24px'}
            paddingX={'24px'}
            paddingTop={'16px'}
            paddingBottom={'calc(18px + env(safe-area-inset-bottom))'}
            backgroundColor={'rgba(17,17,17,0.025)'}
            boxShadow={'inset 0 1px 0 rgba(255,255,255,0.22)'}
          >
            <Stack spacing={'8px'} width={'100%'} alignItems={'stretch'}>
              <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
                <Box
                  width={'2px'}
                  height={'2px'}
                  marginTop={'7px'}
                  borderRadius={'999px'}
                  backgroundColor={'currentColor'}
                  opacity={0.64}
                  flexShrink={0}
                />
                <Text
                  className="text-main-light-3 dark:text-main-dark-3"
                  fontSize={'12px'}
                  textAlign={'left'}
                  lineHeight={'18px'}
                >
                  {t('myPhotoCardDiary.footerNoticeMembers', {
                    defaultValue: '최애가 늘어나면 바인더도 함께 늘어나요'
                  })}
                </Text>
              </Box>

              <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
                <Box
                  width={'2px'}
                  height={'2px'}
                  marginTop={'7px'}
                  borderRadius={'999px'}
                  backgroundColor={'currentColor'}
                  opacity={0.64}
                  flexShrink={0}
                />
                <Text
                  className="text-main-light-3 dark:text-main-dark-3"
                  fontSize={'12px'}
                  textAlign={'left'}
                  lineHeight={'18px'}
                >
                  {t('myPhotoCardDiary.footerNoticeArtist', {
                    defaultValue: '아티스트 전체 바인더는 현재 지원하지 않아요'
                  })}
                </Text>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Page>
  );
}
